import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import ApiCall from "../../utils/apicall";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  Card,
  Stack,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Container,
  TableContainer,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Checkbox,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { HomeRounded } from "@mui/icons-material";
import Iconify from "../../components/iconify";
import Label from "../../components/label";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLORS = {
  draft: "default",
  active: "success",
  deactivated: "error",
};

function ReleasePackages() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: userPermissions } = loginUser?.decodedToken ?? {}; // review-fix F-06

  const canManagePackages = hasPermission(userPermissions, PERMISSIONS.VIEW_TASK);

  // State
  const [job, setJob] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [openNewPackageModal, setOpenNewPackageModal] = useState(false);
  const [selectedPackageDetail, setSelectedPackageDetail] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [approver, setApprover] = useState(loginUser?.decodedToken?.id);

  const [formData, setFormData] = useState({
    packageNumber: "",
    name: "",
    description: "",
  });

  // Fetch job
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await ApiCall.get(`/job/${jobId}`);
        setJob(res.data?.data?.job);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load job");
      }
    };

    if (jobId) fetchJob();
  }, [jobId]);

  // Fetch packages
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (jobId) fetchPackages();
  }, [jobId]);

  // Fetch users for approver dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await ApiCall.get("/user");
        const raw = res.data?.data?.users;
        setUsers(Array.isArray(raw) ? raw : []);
      } catch (err) {
        // non-critical — approver defaults to current user
      }
    };
    fetchUsers();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await ApiCall.get(`/release-packages/job/${jobId}`);
      setPackages(res.data?.data?.packages || []);
      setApiAvailable(true);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404 || err.message?.includes("404")) {
        // API endpoint not available yet
        setPackages([]);
        setApiAvailable(false);
      } else {
        setError(err.response?.data?.message || "Failed to load packages");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch available items for new package
  const handleOpenNewPackage = async () => {
    try {
      const res = await ApiCall.get(
        `/fabricated-items/get-fabricated-item/${jobId}`
      );
      setAvailableItems(res.data?.data?.FabricatedItems || []);
      setSelectedItems([]);
      setFormData({
        packageNumber: "",
        name: "",
        description: "",
      });
      setOpenNewPackageModal(true);
    } catch (err) {
      setError("Failed to load available items");
    }
  };

  // Handle form input
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Toggle item selection
  const handleToggleItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  // Submit new package
  const handleSubmitNewPackage = async () => {
    if (!formData.packageNumber.trim() || !formData.name.trim()) {
      setError("Package number and name are required");
      return;
    }

    setSubmitLoading(true);
    try {
      const res = await ApiCall.post("/release-packages", {
        packageNumber: formData.packageNumber,
        name: formData.name,
        description: formData.description,
        jobId: jobId,
        itemIds: selectedItems,
        status: "draft",
      });

      setPackages((prev) => [...prev, res.data?.data?.package]);
      setOpenNewPackageModal(false);
      setFormData({
        packageNumber: "",
        name: "",
        description: "",
      });
      setSelectedItems([]);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create package");
    } finally {
      setSubmitLoading(false);
    }
  };

  // View/Edit package
  const handleViewPackage = async (packageId) => {
    try {
      const res = await ApiCall.get(`/release-packages/${packageId}`);
      setSelectedPackageDetail(res.data?.data?.package);
    } catch (err) {
      setError("Failed to load package details");
    }
  };

  // Activate package
  const handleActivatePackage = async (packageId) => {
    try {
      await ApiCall.patch(`/release-packages/${packageId}/activate`, {
        userId: approver,
      });
      const res = await ApiCall.get(`/release-packages/job/${jobId}`);
      setPackages(res.data?.data?.packages || []);
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to activate package");
    }
  };

  // Deactivate package
  const handleDeactivatePackage = async (packageId) => {
    try {
      await ApiCall.patch(`/release-packages/${packageId}/deactivate`);
      const res = await ApiCall.get(`/release-packages/job/${jobId}`);
      setPackages(res.data?.data?.packages || []);
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to deactivate package");
    }
  };

  // Delete package
  const handleDeletePackage = async (packageId) => {
    try {
      await ApiCall.delete(`/release-packages/${packageId}`);
      setPackages((prev) => prev.filter((p) => p.id !== packageId));
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete package");
    }
  };

  if (!canManagePackages) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          You do not have permission to view this page.
        </Typography>
      </Container>
    );
  }

  // Count unlinked items
  const allJobItems = availableItems.length;
  const linkedItems = packages.reduce((sum, pkg) => sum + (pkg.itemCount ?? pkg.ReleasePackageItems?.length ?? 0), 0);
  const unlinkedItems = allJobItems - linkedItems;

  return (
    <div>
      <Helmet>
        <title>Release Packages | SFM</title>
      </Helmet>

      <Container maxWidth="lg">
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <div>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HomeRounded color="inherit" />
                <Typography variant="body1" color="textPrimary">
                  / Release Packages — Engineering Control Gate
                </Typography>
              </Stack>
            </Breadcrumbs>
            <Typography variant="h4">{job?.name || "Loading..."}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              Engineering must create and activate a release package before fabricators can work on the included items. Draft packages have no effect — only Active packages authorize fabrication.
            </Typography>
          </div>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-ios-back-outline" />}
            onClick={() => navigate(`/detail-Job/${jobId}`)}
          >
            Back
          </Button>
        </Stack>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "error.light" }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {!apiAvailable && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Release Package APIs are deploying. Once online, use this module to group items into engineering release packages and gate fabrication by activation status.
          </Alert>
        )}

        {/* Warning Banner */}
        {apiAvailable && unlinkedItems > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            ⚠️ {unlinkedItems} item{unlinkedItems > 1 ? "s are" : " is"} not covered by any active release package — fabrication on {unlinkedItems > 1 ? "these items" : "this item"} is not yet authorized.
          </Alert>
        )}

        {/* Add Button */}
        {apiAvailable && (
          <Stack sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleOpenNewPackage}
            >
              New Package
            </Button>
          </Stack>
        )}

        {/* Table */}
        <Card>
          {loading ? (
            <Typography sx={{ p: 3 }}>Loading packages...</Typography>
          ) : !apiAvailable ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                Module Deploying
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Release Package APIs will be live after the next deploy. Once active, this is where engineering creates packages, links items, and authorizes fabrication by activating a package.
              </Typography>
            </Paper>
          ) : packages.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                No release packages yet for this job
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Create a package, link the items engineering has approved, then activate it. Fabricators cannot start work on items until they appear in an active release package.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleOpenNewPackage}
              >
                Create First Release Package
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <SmartTable tableId="release-packages" lockedColumns={["Package Number", "Status"]}>
                <TableHead>
                  <TableRow>
                    <TableCell>Package Number</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packages.map((pkg) => (
                    <TableRow key={pkg.id} hover>
                      <TableCell>{pkg.packageNumber}</TableCell>
                      <TableCell>{pkg.name}</TableCell>
                      <TableCell>
                        <Label color={STATUS_COLORS[pkg.status] || "default"}>
                          {pkg.status}
                        </Label>
                      </TableCell>
                      <TableCell align="right">
                        {pkg.itemCount ?? pkg.ReleasePackageItems?.length ?? 0}
                      </TableCell>
                      <TableCell>
                        {pkg.createdAt
                          ? new Date(pkg.createdAt).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ display: "flex", gap: 1 }}>
                        {pkg.status === "draft" && (
                          <MenuItem
                            onClick={() => {
                              setApprover(loginUser?.decodedToken?.id);
                              setConfirmDialog({
                                title: "Activate Release Package?",
                                message: `Activating Package ${pkg.packageNumber} (${pkg.name}) authorizes fabrication of all linked items. This action is logged. Select the approving engineer below.`,
                                onConfirm: () => handleActivatePackage(pkg.id),
                                requiresApprover: true,
                              });
                            }}
                          >
                            <Iconify icon="eva:checkmark-outline" />
                          </MenuItem>
                        )}
                        {pkg.status === "active" && (
                          <MenuItem
                            onClick={() =>
                              setConfirmDialog({
                                title: "Deactivate Release Package?",
                                message: `Deactivating Package ${pkg.packageNumber} will remove fabrication authorization for all linked items. Fabricators in progress will be blocked. Continue?`,
                                onConfirm: () => handleDeactivatePackage(pkg.id),
                              })
                            }
                          >
                            <Iconify icon="eva:alert-circle-outline" />
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => handleViewPackage(pkg.id)}>
                          <Iconify icon="eva:eye-outline" />
                        </MenuItem>
                        <MenuItem
                          sx={{ color: "error.main" }}
                          onClick={() =>
                            setConfirmDialog({
                              title: "Delete Package?",
                              message: `Delete package ${pkg.packageNumber}? This cannot be undone.`,
                              onConfirm: () => handleDeletePackage(pkg.id),
                            })
                          }
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </MenuItem>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SmartTable>
            </TableContainer>
          )}
        </Card>
      </Container>

      {/* New Package Modal */}
      <Dialog
        open={openNewPackageModal}
        onClose={() => setOpenNewPackageModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Release Package</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Group items that engineering has cleared for fabrication. Once created, activate the package to authorize your fabricators to begin work.
          </Typography>
          <TextField
            label="Package Number * (e.g. RP-001, REL-A)"
            name="packageNumber"
            value={formData.packageNumber}
            onChange={handleFormChange}
            fullWidth
            required
          />
          <TextField
            label="Name *"
            name="name"
            value={formData.name}
            onChange={handleFormChange}
            fullWidth
            required
          />
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            multiline
            rows={3}
            fullWidth
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            Link Items to This Package
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
            Select all items included in this engineering release. Only items in an active package can move through fabrication.
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: "auto", border: "1px solid #ddd", p: 1 }}>
            {availableItems.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No fabricated items on this job yet — create items first, then return to build release packages.
              </Typography>
            ) : (
              availableItems.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Checkbox
                    checked={selectedItems.includes(item.id)}
                    onChange={() => handleToggleItem(item.id)}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {item.name || item.mark}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
          <Typography variant="caption" color="textSecondary">
            {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""} selected
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewPackageModal(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitNewPackage}
            variant="contained"
            disabled={submitLoading}
          >
            {submitLoading ? "Creating..." : "Create Package"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Package Detail Modal */}
      {selectedPackageDetail && (
        <Dialog
          open={!!selectedPackageDetail}
          onClose={() => setSelectedPackageDetail(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{selectedPackageDetail.name}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Package Number
                </Typography>
                <Typography variant="body1">
                  {selectedPackageDetail.packageNumber}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                <Label color={STATUS_COLORS[selectedPackageDetail.status]}>
                  {selectedPackageDetail.status}
                </Label>
              </Box>
              {selectedPackageDetail.description && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {selectedPackageDetail.description}
                  </Typography>
                </Box>
              )}
              {selectedPackageDetail.revisionNotes && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Revision Notes
                  </Typography>
                  <Typography variant="body1">
                    {selectedPackageDetail.revisionNotes}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Linked Items ({selectedPackageDetail.ReleasePackageItems?.length || 0})
                </Typography>
                <Stack spacing={1}>
                  {(selectedPackageDetail.ReleasePackageItems || []).map((rpi) => (
                    <Paper key={rpi.id} sx={{ p: 1 }}>
                      <Typography variant="body2">
                        {rpi.fabricated_items_perjob?.name || rpi.fabricatedItemId}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedPackageDetail(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <Dialog
          open={!!confirmDialog}
          onClose={() => setConfirmDialog(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: confirmDialog.requiresApprover ? 2 : 0 }}>
              {confirmDialog.message}
            </Typography>
            {confirmDialog.requiresApprover && (
              <FormControl fullWidth size="small">
                <InputLabel>Approved by</InputLabel>
                <Select
                  label="Approved by"
                  value={approver}
                  onChange={(e) => setApprover(e.target.value)}
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button
              onClick={() => {
                confirmDialog.onConfirm();
              }}
              variant="contained"
              color="primary"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

export default ReleasePackages;
